export type ChartTimeTick = {
  value: number;
  position: number;
};

export type ChartTimeDayMarkers = {
  boundaries: ChartTimeTick[];
  labels: ChartTimeTick[];
};

export function chartTimeTicks(start: number, end: number, divisions = 4): ChartTimeTick[] {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [{ value: start, position: 0 }];
  const safeDivisions = Math.max(1, Math.floor(divisions));
  return Array.from({ length: safeDivisions + 1 }, (_, index) => ({
    value: start + (end - start) * index / safeDivisions,
    position: index / safeDivisions,
  }));
}

type CalendarDate = { year: number; month: number; day: number };
type ZonedDateTime = CalendarDate & { hour: number; minute: number; second: number };
const calendarFormatters = new Map<string, Intl.DateTimeFormat>();

function calendarFormatter(timeZone: string) {
  const existing = calendarFormatters.get(timeZone);
  if (existing) return existing;
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
      timeZone,
    });
    calendarFormatters.set(timeZone, formatter);
    return formatter;
  } catch {
    return null;
  }
}

function calendarDateAt(timestamp: number, timeZone: string): ZonedDateTime | null {
  const formatter = calendarFormatter(timeZone);
  if (!formatter) return null;
  try {
    const parts = formatter.formatToParts(new Date(timestamp));
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const year = Number(values.year);
    const month = Number(values.month);
    const day = Number(values.day);
    const hour = Number(values.hour);
    const minute = Number(values.minute);
    const second = Number(values.second);
    return Number.isInteger(year) && Number.isInteger(month) && Number.isInteger(day) && Number.isInteger(hour) && Number.isInteger(minute) && Number.isInteger(second)
      ? { year, month, day, hour, minute, second }
      : null;
  } catch {
    return null;
  }
}

function localMidnightTimestamp(date: CalendarDate, timeZone: string) {
  const target = Date.UTC(date.year, date.month - 1, date.day);
  let candidate = target;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const displayed = calendarDateAt(candidate, timeZone);
    if (!displayed) return Number.NaN;
    const displayedTimestamp = Date.UTC(displayed.year, displayed.month - 1, displayed.day, displayed.hour, displayed.minute, displayed.second);
    if (displayedTimestamp === target) return candidate;
    candidate += target - displayedTimestamp;
  }
  return Number.NaN;
}

function nextCalendarDate(date: CalendarDate): CalendarDate {
  const next = new Date(Date.UTC(date.year, date.month - 1, date.day + 1));
  return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() };
}

/**
 * Returns local calendar-day transitions for a server-authoritative time range.
 * Boundaries exclude the selected range edges. Labels are centered in their
 * visible calendar day and omit very short partial days at the range edges.
 */
export function chartTimeDayMarkers(start: number, end: number, timeZone: string): ChartTimeDayMarkers {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return { boundaries: [], labels: [] };
  const initialDate = calendarDateAt(start, timeZone);
  if (!initialDate) return { boundaries: [], labels: [] };
  let date: CalendarDate = initialDate;
  let dayStart = localMidnightTimestamp(date, timeZone);
  if (!Number.isFinite(dayStart)) return { boundaries: [], labels: [] };

  const boundaries: ChartTimeTick[] = [];
  const labels: ChartTimeTick[] = [];
  const rangeLength = end - start;
  while (dayStart < end) {
    const nextDate = nextCalendarDate(date);
    const nextDayStart = localMidnightTimestamp(nextDate, timeZone);
    if (!Number.isFinite(nextDayStart) || nextDayStart <= dayStart) break;
    const visibleStart = Math.max(start, dayStart);
    const visibleEnd = Math.min(end, nextDayStart);
    if (visibleEnd - visibleStart >= (nextDayStart - dayStart) / 4) {
      labels.push({ value: dayStart, position: ((visibleStart + visibleEnd) / 2 - start) / rangeLength });
    }
    if (nextDayStart > start && nextDayStart < end) {
      boundaries.push({ value: nextDayStart, position: (nextDayStart - start) / rangeLength });
    }
    date = nextDate;
    dayStart = nextDayStart;
  }

  return boundaries.length === 0
    ? { boundaries, labels: [] }
    : { boundaries, labels };
}
